import { default as React } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { makeStyles } from '@mui/styles';
import { DocsHelpIcon } from './components';
import Alert from '@mui/material/Alert';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  repoExistsWarning: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(211, 47, 47, 0.15)'
        : 'rgba(211, 47, 47, 0.1)',
    borderLeft: `3px solid ${theme.palette.error.main}`,
    borderRadius: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  repoExistsText: {
    fontSize: '0.875rem',
    color: theme.palette.error.main,
    fontWeight: 500,
  },
}));

// Cache for solutions config - loaded once per session
let solutionsConfigCache: Record<string, any> | null = null;
let solutionsConfigPromise: Promise<Record<string, any>> | null = null;
// Tracks whether the cached response was fetched with an auth token.
// If a token becomes available after an unauthenticated cache hit, the cache is invalidated.
let solutionsConfigFetchedWithToken = false;

// Reset the cache between test runs to prevent cross-test contamination.
// Only imported and used by the test file.
export const __resetSolutionsCache = () => {
  solutionsConfigCache = null;
  solutionsConfigPromise = null;
  solutionsConfigFetchedWithToken = false;
};

// Helper function to load solutions config (with caching)
async function loadSolutionsConfig(
  backendUrl: string,
  token?: string,
): Promise<Record<string, any>> {
  // If the cache was populated without a token but we now have one, invalidate it
  // so the authenticated request can be made.
  if (solutionsConfigCache && token && !solutionsConfigFetchedWithToken) {
    solutionsConfigCache = null;
    solutionsConfigPromise = null;
  }

  // Return cached config if available
  if (solutionsConfigCache) {
    return solutionsConfigCache;
  }

  // If already loading, return the same promise
  if (solutionsConfigPromise) {
    return solutionsConfigPromise;
  }

  // Start loading
  solutionsConfigPromise = (async () => {
    try {
      const apiUrl = `${backendUrl}/api/github-repo-api/solutions`;
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        solutionsConfigFetchedWithToken = true;
      }
      const response = await fetch(apiUrl, { headers });

      if (!response.ok) {
        throw new Error(
          `Failed to load solutions config: ${response.statusText}`,
        );
      }

      const config = await response.json();
      solutionsConfigCache = config; // Cache the result
      return config;
    } catch (error) {
      solutionsConfigPromise = null; // Reset promise on error
      throw error;
    }
  })();

  return solutionsConfigPromise;
}

// Result type for short key lookup
interface ShortKeyResult {
  shortKey: string;
  isConfigured: boolean;
}

// Helper function to get short key from solutions config
async function getSolutionShortKey(
  solution: string,
  backendUrl: string,
  token?: string,
): Promise<ShortKeyResult> {
  try {
    // Other Internal Tools path: Business Unit repos never receive a prefix
    if (solution.startsWith('other-internal-tools/')) {
      return { shortKey: '', isConfigured: false };
    }

    // Extract solution name from entity reference FIRST (e.g., "system:default/biotherapies" -> "biotherapies")
    const solutionKey = solution.includes('/')
      ? solution.split('/').pop()
      : solution;

    if (!solutionKey) {
      // Return empty string for consistency with IDP-800 (no auto-generated prefix)
      return { shortKey: '', isConfigured: false };
    }

    // Load solutions config (cached)
    const config = await loadSolutionsConfig(backendUrl, token);

    // Check github-repo-config.short-key (NEW LOCATION ONLY)
    if (
      config[solutionKey] &&
      config[solutionKey]['github-repo-config'] &&
      config[solutionKey]['github-repo-config']['short-key']
    ) {
      return {
        shortKey: config[solutionKey]['github-repo-config']['short-key'],
        isConfigured: true,
      };
    }

    // No configured short-key - return empty string (no prefix applied)
    // Per IDP-800: Only apply prefix if explicitly configured
    return {
      shortKey: '',
      isConfigured: false,
    };
  } catch (error) {
    // On error, return empty string (no prefix applied)
    // Per IDP-800: Only apply prefix if explicitly configured
    return {
      shortKey: '',
      isConfigured: false,
    };
  }
}

// Helper: compute the canonical prefix string for the current state (non-Terraform)
// Returns the prefix that would be auto-applied (e.g., "ERM.", "ERM.Playground.", "")
function computeStandardPrefix(
  solution: string | undefined,
  shortKey: string,
  shortKeyLoaded: boolean,
  isDropdownSolution: boolean | null,
  repoType: string,
): string {
  if (solution && (isDropdownSolution === null || !shortKeyLoaded)) {
    // Still loading
    return '';
  }
  if (solution && shortKeyLoaded && shortKey) {
    return repoType === 'Playground'
      ? `${shortKey}.Playground.`
      : `${shortKey}.`;
  }
  if (solution && shortKeyLoaded && !shortKey) {
    // No configured prefix
    return repoType === 'Playground' ? 'Playground.' : '';
  }
  // No solution selected
  return repoType === 'Playground' ? 'Playground.' : '';
}

export const RepoNameField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const [isDropdownSolution, setIsDropdownSolution] = useState<boolean | null>(
    null,
  );
  const classes = useStyles();

  // True when the component mounts with pre-existing formData (e.g. after a templateSource
  // switch causes RJSF to remount the field). Used by the prefix-update effect to skip
  // re-applying the prefix and preserve the user's typed value exactly.
  const hasExistingFormDataRef = useRef(!!formData);

  // IDP-1060: repoName now holds the FULL repository name (prefix + suffix combined).
  // When a solution is selected, the configured prefix is auto-populated into this field
  // as the default suggestion. The user can modify, replace, or delete the prefix freely.
  const [repoName, setRepoName] = useState('');
  const [shortKey, setShortKey] = useState<string>('');
  const [isShortKeyConfigured, setIsShortKeyConfigured] =
    useState<boolean>(true);
  const [shortKeyLoaded, setShortKeyLoaded] = useState<boolean>(false);
  const [repoExists, setRepoExists] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  // Track the last auto-applied prefix so we can update the field when solution changes.
  // This lets us replace the old prefix with the new one while preserving the user's suffix.
  const lastAppliedPrefixRef = useRef<string>('');

  // Mirror repoName into a ref so the prefix-update effect can read the current value
  // without including repoName in its dependency array (which would re-run on every keystroke).
  const repoNameRef = useRef<string>('');
  useEffect(() => {
    repoNameRef.current = repoName;
  }, [repoName]);

  // Get values from sibling fields via formContext
  const allFormData = formContext?.formData || {};
  const repoType = allFormData.repoType || 'standard';
  const solution = allFormData.solution;
  const templateSource = allFormData.templateSource || '';
  const standardTemplate = allFormData.standardTemplate || '';
  const provider = allFormData.provider || '';
  // Get GitHub organization and token from host field (set by GithubSsoLogin)
  const hostGithubOrg = allFormData.host?.githubOrg || '';
  const githubToken = allFormData.host?.token || '';
  // Only consider Terraform templates when templateSource is 'standard'
  // This prevents stale standardTemplate values from affecting the UI when switching to GitHub templates
  const isTerraformInfraTemplate =
    templateSource === 'standard' &&
    standardTemplate === 'terraform-infrastructure';
  const isTerraformModuleTemplate =
    templateSource === 'standard' && standardTemplate === 'terraform-module';
  const isTerraform = isTerraformInfraTemplate || isTerraformModuleTemplate;

  // Get backend URL from config
  const backendUrl = window.location.origin.replace(':3000', ':7007');

  // Load short key when solution changes
  useEffect(() => {
    // Use cancelled flag to prevent race conditions when solution changes quickly
    let cancelled = false;

    const loadSolutionInfo = async () => {
      if (solution) {
        // Reset loading state when solution changes
        setShortKeyLoaded(false);
        // All solutions now come from dropdown with entity reference format
        setIsDropdownSolution(true);
        const result = await getSolutionShortKey(
          solution,
          backendUrl,
          githubToken,
        );
        // Only update state if this request wasn't cancelled by a newer one
        if (!cancelled) {
          setShortKey(result.shortKey);
          setIsShortKeyConfigured(result.isConfigured);
          setShortKeyLoaded(true);
        }
      } else {
        if (!cancelled) {
          setShortKey('');
          setIsShortKeyConfigured(true); // No warning when no solution selected
          setShortKeyLoaded(true); // Not loading when no solution
          setIsDropdownSolution(null);
        }
      }
    };

    loadSolutionInfo();

    // Cleanup: cancel this effect if solution changes before async completes
    return () => {
      cancelled = true;
    };
  }, [solution, backendUrl, githubToken]);

  // IDP-1060: When the prefix changes (solution selected or shortKey loaded),
  // update the text field to show the new prefix + whatever suffix the user had typed.
  // This preserves the user's suffix when they change solution.
  useEffect(() => {
    // Only auto-populate for standard (non-Terraform) templates
    if (isTerraformInfraTemplate || isTerraformModuleTemplate) {
      return;
    }
    if (!shortKeyLoaded) {
      return;
    }

    const newPrefix = computeStandardPrefix(
      solution,
      shortKey,
      shortKeyLoaded,
      isDropdownSolution,
      repoType,
    );

    const prevPrefix = lastAppliedPrefixRef.current;

    if (newPrefix === prevPrefix) {
      // Prefix hasn't changed — don't interfere with user's typing
      return;
    }

    // Extract the suffix the user had typed beyond the old prefix.
    // Read from the ref rather than state to avoid including repoName in the deps array
    // (which would re-run this effect on every keystroke and fight the user's input).
    const currentValue = repoNameRef.current;

    // Guard 1: value already starts with the correct prefix — just track it, don't re-apply.
    if (newPrefix && currentValue.startsWith(newPrefix)) {
      lastAppliedPrefixRef.current = newPrefix;
      return;
    }

    // Guard 2: component remounted with pre-existing formData (e.g. templateSource switch
    // causes RJSF to unmount/remount this field, resetting lastAppliedPrefixRef to '').
    // Preserve the user's typed value exactly — do not prepend the prefix again.
    if (prevPrefix === '' && hasExistingFormDataRef.current) {
      lastAppliedPrefixRef.current = newPrefix;
      return;
    }

    let suffix = currentValue;
    if (prevPrefix && currentValue.startsWith(prevPrefix)) {
      suffix = currentValue.slice(prevPrefix.length);
    } else if (prevPrefix && currentValue === '') {
      suffix = '';
    }

    // Build the new suggested value: newPrefix + preserved suffix
    const newValue = newPrefix + suffix;
    lastAppliedPrefixRef.current = newPrefix;

    setRepoName(newValue);
    onChange(newValue || '');
  }, [
    shortKey,
    shortKeyLoaded,
    solution,
    repoType,
    isDropdownSolution,
    onChange,
    isTerraformInfraTemplate,
    isTerraformModuleTemplate,
  ]);

  // Initialize repoName from formData (e.g., when user navigates back to the step)
  useEffect(() => {
    // Restore from sessionStorage when returning to form
    if (!formData && !initialized) {
      const savedFormState = sessionStorage.getItem('github-repo-form-state');

      if (savedFormState) {
        try {
          const parsedState = JSON.parse(savedFormState);
          const ONE_HOUR = 60 * 60 * 1000;
          const isStale =
            parsedState.timestamp &&
            Date.now() - parsedState.timestamp > ONE_HOUR;

          if (!isStale && parsedState.repoName) {
            setRepoName(parsedState.repoName);
            onChange(parsedState.repoName);
            setInitialized(true);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to restore repoName field:', error);
        }
      }
    }

    if (!initialized && formData) {
      // IDP-1060: formData already contains the full name — use it directly.
      // Do NOT pre-warm lastAppliedPrefixRef here. The regex /^[A-Z]+\./  only captures
      // the first uppercase segment (e.g. "AI." for "AI.Scribe.Test"), which is wrong for
      // multi-segment short keys (e.g. "AI.Scribe"). That wrong prevPrefix causes suffix
      // extraction to pull "Scribe.Test" instead of "Test", doubling the prefix on remount.
      // The guard in the prefix-update effect (currentValue.startsWith(newPrefix)) already
      // handles this correctly: when the component remounts with formData already containing
      // the correct prefix, the guard fires and returns without re-applying the prefix.
      setRepoName(formData);
      setInitialized(true);
    } else if (!initialized && !formData) {
      setInitialized(true);
    }
  }, [formData, initialized, onChange]);

  // Transform existing repoName to lowercase when Terraform template is selected
  // This handles the case where user types uppercase first, then selects Terraform
  useEffect(() => {
    if (isTerraform && repoName && repoName !== repoName.toLowerCase()) {
      const lowercaseName = repoName.toLowerCase();
      setRepoName(lowercaseName);
      onChange(lowercaseName);
    }
  }, [isTerraform, repoName, onChange]);

  // Validate the full name whenever it changes
  useEffect(() => {
    if (repoName) {
      // Use lowercase-only pattern for Terraform repos; allow mixed case for regular repos
      const pattern = isTerraform
        ? /^[a-z][a-z0-9-_.]*$/
        : /^[a-zA-Z][a-zA-Z0-9-_.]*$/;
      if (/\s/.test(repoName)) {
        setValidationError(
          'Repository name cannot contain spaces. Use hyphens (-) or underscores (_) instead.',
        );
      } else if (!pattern.test(repoName)) {
        setValidationError(
          isTerraform
            ? 'Repository name must start with a letter and contain only lowercase letters, numbers, dots (.), hyphens (-), and underscores (_).'
            : 'Repository name must start with a letter and contain only letters, numbers, dots (.), hyphens (-), and underscores (_).',
        );
      } else {
        setValidationError('');
      }
    } else {
      setValidationError('');
    }
  }, [repoType, solution, repoName, isTerraform]);

  // For Terraform templates: compute the full name from prefix + repoName suffix,
  // update onChange, and check repo existence.
  // For standard templates: repoName IS the full name — just check repo existence.
  useEffect(() => {
    let finalName = repoName;

    // SPECIAL CASE: Terraform Infrastructure template selected (ONLY for Standard repos)
    if (isTerraformInfraTemplate) {
      if (repoType !== 'Standard') {
        setValidationError(
          'Terraform templates are only available for Standard repositories.',
        );
        onChange('');
        setRepoExists(false);
        return undefined;
      }

      // Clear Terraform-unrelated validation errors
      setValidationError(prev =>
        prev.includes('Terraform') ||
        prev.includes('select a solution') ||
        prev.includes('enter a provider')
          ? ''
          : prev,
      );

      if (!solution) {
        setValidationError(
          'Please select a solution above to generate the Terraform repository name.',
        );
        onChange('');
        setRepoExists(false);
        return undefined;
      }
      if (!shortKeyLoaded) {
        onChange('');
        setRepoExists(false);
        return undefined;
      }

      // Compose Terraform infra name
      if (shortKey) {
        const shortKeyLower = shortKey.toLowerCase();
        finalName = repoName
          ? `terraform-${shortKeyLower}-${repoName.toLowerCase()}`
          : `terraform-${shortKeyLower}`;
      } else {
        const solutionName = solution?.includes('/')
          ? solution.split('/').pop()?.toLowerCase()
          : solution?.toLowerCase();
        if (repoName && solutionName) {
          finalName = `terraform-${solutionName}-${repoName.toLowerCase()}`;
        } else if (repoName) {
          finalName = `terraform-${repoName.toLowerCase()}`;
        } else if (solutionName) {
          finalName = `terraform-${solutionName}`;
        } else {
          finalName = 'terraform';
        }
      }

      onChange(finalName);

      if (finalName) {
        const checkRepo = async () => {
          try {
            const orgName = hostGithubOrg?.includes('/')
              ? hostGithubOrg.split('/').pop()
              : hostGithubOrg;
            const orgParam = orgName
              ? `&org=${encodeURIComponent(orgName)}`
              : '';
            const headers: Record<string, string> = {};
            if (githubToken) {
              headers.Authorization = `Bearer ${githubToken}`;
            }
            const response = await fetch(
              `${backendUrl}/api/github-repo-api/check-repo?name=${encodeURIComponent(
                finalName,
              )}${orgParam}`,
              { headers },
            );
            if (response.ok) {
              const data = await response.json();
              setRepoExists(data.exists);
            }
          } catch (_error) {
            setRepoExists(false);
          }
        };
        const timer = setTimeout(checkRepo, 500);
        return () => clearTimeout(timer);
      }
      setRepoExists(false);
      return undefined;
    }

    // SPECIAL CASE: Terraform Module template selected (ONLY for Standard repos)
    if (isTerraformModuleTemplate) {
      if (repoType !== 'Standard') {
        setValidationError(
          'Terraform templates are only available for Standard repositories.',
        );
        onChange('');
        setRepoExists(false);
        return undefined;
      }

      setValidationError(prev =>
        prev.includes('Terraform') ||
        prev.includes('select a solution') ||
        prev.includes('enter a provider')
          ? ''
          : prev,
      );

      if (!provider) {
        setValidationError(
          'Please enter a provider above to generate the Terraform module repository name.',
        );
        onChange('');
        setRepoExists(false);
        return undefined;
      }
      if (!repoName) {
        onChange('');
        setRepoExists(false);
        return undefined;
      }

      finalName = `terraform-${provider.toLowerCase()}-${repoName.toLowerCase()}`;
      onChange(finalName);

      if (finalName) {
        const checkRepo = async () => {
          try {
            const orgName = hostGithubOrg?.includes('/')
              ? hostGithubOrg.split('/').pop()
              : hostGithubOrg;
            const orgParam = orgName
              ? `&org=${encodeURIComponent(orgName)}`
              : '';
            const headers: Record<string, string> = {};
            if (githubToken) {
              headers.Authorization = `Bearer ${githubToken}`;
            }
            const response = await fetch(
              `${backendUrl}/api/github-repo-api/check-repo?name=${encodeURIComponent(
                finalName,
              )}${orgParam}`,
              { headers },
            );
            if (response.ok) {
              const data = await response.json();
              setRepoExists(data.exists);
            }
          } catch (_error) {
            setRepoExists(false);
          }
        };
        const timer = setTimeout(checkRepo, 500);
        return () => clearTimeout(timer);
      }
      setRepoExists(false);
      return undefined;
    }

    // STANDARD (non-Terraform) LOGIC
    // IDP-1060: repoName IS the full name — pass it directly to onChange.
    // No prefix composition needed here; the prefix-population useEffect handles auto-suggest.
    onChange(repoName || '');

    // Check if repository exists
    if (repoName && !repoName.includes('[')) {
      const checkRepo = async () => {
        try {
          const orgName = hostGithubOrg?.includes('/')
            ? hostGithubOrg.split('/').pop()
            : hostGithubOrg;
          const orgParam = orgName ? `&org=${encodeURIComponent(orgName)}` : '';
          const headers: Record<string, string> = {};
          if (githubToken) {
            headers.Authorization = `Bearer ${githubToken}`;
          }
          const response = await fetch(
            `${backendUrl}/api/github-repo-api/check-repo?name=${encodeURIComponent(
              repoName,
            )}${orgParam}`,
            { headers },
          );
          if (response.ok) {
            const data = await response.json();
            setRepoExists(data.exists);
          }
        } catch (_error) {
          setRepoExists(false);
        }
      };
      const timer = setTimeout(checkRepo, 500);
      return () => clearTimeout(timer);
    }
    setRepoExists(false);
    return undefined;
  }, [
    repoName,
    repoType,
    solution,
    provider,
    shortKey,
    shortKeyLoaded,
    backendUrl,
    onChange,
    isTerraformInfraTemplate,
    isTerraformModuleTemplate,
    hostGithubOrg,
    githubToken,
  ]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      // Validate for spaces and invalid characters
      if (/\s/.test(rawValue)) {
        setValidationError(
          'Repository name cannot contain spaces. Use hyphens (-) or underscores (_) instead.',
        );
        return; // Don't update the value
      }

      // Use lowercase-only validation for Terraform repos; allow mixed case for regular repos
      const pattern = isTerraform
        ? /^[a-z][a-z0-9-_.]*$/
        : /^[a-zA-Z][a-zA-Z0-9-_.]*$/;
      if (
        rawValue &&
        !pattern.test(isTerraform ? rawValue.toLowerCase() : rawValue)
      ) {
        setValidationError(
          isTerraform
            ? 'Repository name must start with a letter and contain only lowercase letters, numbers, dots (.), hyphens (-), and underscores (_).'
            : 'Repository name must start with a letter and contain only letters, numbers, dots (.), hyphens (-), and underscores (_).',
        );
        // Still allow the change so user can correct it
      } else {
        setValidationError('');
      }

      // Only force lowercase for Terraform repos; preserve original case for regular repos
      const finalValue = isTerraform ? rawValue.toLowerCase() : rawValue;

      setRepoName(finalValue);
      // Note: onChange will be called by the repo-check useEffect which depends on repoName
    },
    [isTerraform],
  );

  const description = uiSchema?.['ui:description'] || schema.description;
  const help = uiSchema?.['ui:help'];
  const placeholder = uiSchema?.['ui:placeholder'] || 'my-service';
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  // Get helper text - prefer ui:help over description to avoid duplication
  const getHelperText = () => {
    if (rawErrors?.length || validationError) {
      return undefined;
    }
    if (help) {
      return `💡 ${help}`;
    }
    if (!isTerraformInfraTemplate && !isTerraformModuleTemplate) {
      const currentPrefix = computeStandardPrefix(
        solution,
        shortKey,
        shortKeyLoaded,
        isDropdownSolution,
        repoType,
      );
      if (currentPrefix) {
        return (
          description ||
          `The prefix "${currentPrefix}" has been auto-applied. You can modify or delete it.`
        );
      }
    }
    return description;
  };

  // Compute placeholder text
  const getPlaceholder = () => {
    if (isTerraformInfraTemplate) {
      if (!solution) return 'Select a solution first';
      if (!shortKeyLoaded) return 'Loading...';
      let shortKeyLower: string | undefined;
      if (shortKey) {
        shortKeyLower = shortKey.toLowerCase();
      } else if (solution?.includes('/')) {
        shortKeyLower = solution.split('/').pop()?.toLowerCase();
      } else {
        shortKeyLower = solution?.toLowerCase();
      }
      const terraformBase = shortKeyLower
        ? `terraform-${shortKeyLower}`
        : 'terraform';
      return `e.g., ${terraformBase} (suffix optional)`;
    }
    if (isTerraformModuleTemplate) {
      return provider
        ? `e.g., terraform-${provider.toLowerCase()}-bucket`
        : placeholder;
    }
    // Standard: show prefix-aware placeholder
    const currentPrefix = computeStandardPrefix(
      solution,
      shortKey,
      shortKeyLoaded,
      isDropdownSolution,
      repoType,
    );
    if (currentPrefix) {
      return `e.g., ${currentPrefix}my-service`;
    }
    return placeholder;
  };

  return (
    <FormControl
      className={classes.root}
      margin="normal"
      error={(rawErrors && rawErrors.length > 0) || !!validationError}
      fullWidth
    >
      <Box className={classes.labelContainer}>
        <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
          {schema.title || 'Repository Name'}
          {required && ' *'}
        </InputLabel>
        {docsUrl && (
          <DocsHelpIcon
            docsUrl={docsUrl}
            tooltipText="Repository name documentation"
          />
        )}
      </Box>
      {/* IDP-1060: Single editable text field for the full repository name.
          The prefix (e.g., "ERM.") is auto-populated as a suggestion when a solution
          is selected, but the user can freely modify, replace, or delete it. */}
      <TextField
        id={idSchema?.$id}
        value={repoName}
        onChange={handleChange}
        placeholder={getPlaceholder()}
        helperText={getHelperText()}
        required={required}
        error={(rawErrors && rawErrors.length > 0) || !!validationError}
        inputProps={{
          pattern: schema.pattern,
          maxLength: schema.maxLength,
        }}
      />

      {/* Validation error message */}
      {validationError && (
        <FormHelperText error style={{ fontSize: '0.9rem', fontWeight: 500 }}>
          {validationError}
        </FormHelperText>
      )}

      {/* Repository exists warning */}
      {repoExists && repoName && (
        <div className={classes.repoExistsWarning}>
          <span className={classes.repoExistsText}>
            ⚠️ Repository "{repoName}" already exists. Please choose a different
            name.
          </span>
        </div>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error style={{ fontSize: '0.9rem', fontWeight: 500 }}>
          {rawErrors.join(', ')}
        </FormHelperText>
      )}

      {/* Warning if solution not selected for standard repo */}
      {repoType === 'Standard' && !solution && repoName && (
        <Alert severity="warning" style={{ marginTop: 12, borderRadius: 8 }}>
          <strong>⚠️ Solution Required:</strong> Please select a solution above
          to generate the correct repository name with the proper prefix.
        </Alert>
      )}

      {/* Info when solution has no configured short-key (no prefix applied) */}
      {/* Suppressed for Other Internal Tools: absence of prefix is intentional, not a config gap */}
      {solution &&
        !solution.startsWith('other-internal-tools/') &&
        shortKeyLoaded &&
        !isShortKeyConfigured &&
        !shortKey &&
        !isTerraformModuleTemplate &&
        !isTerraformInfraTemplate && (
          <Alert severity="info" style={{ marginTop: 12, borderRadius: 8 }}>
            <strong>ℹ️ No Prefix Applied:</strong> This solution does not have a
            configured prefix, so no prefix will be added to the repository
            name. To add a prefix, refer to the{' '}
            <a
              href="/catalog/default/component/skystage/docs/github-repo-config-management/"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub Repo Config Management documentation
            </a>
            .
          </Alert>
        )}
    </FormControl>
  );
};

export const RepoNameFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'RepoNameField',
    component: RepoNameField,
  }),
);
